import {
  ArrowLeft,
  ArrowRight,
  CalendarBlank,
  MagnifyingGlass,
  User,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { RequireAdmin } from '@/app/auth-guard'
import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { formatDateTime } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { kuniriIconWeight } from '@/lib/icons'

import {
  adminApi,
  type AdminActivityListResponse,
  type AdminRecordFilters,
  type AdminRecordKind,
  type AdminUserListResponse,
} from '../api'

type ListState =
  | { status: 'loading' }
  | {
      status: 'ready'
      users: AdminUserListResponse | null
      activities: AdminActivityListResponse | null
    }
  | { status: 'error'; error: unknown }

const FILTER_KEYS = [
  'status',
  'verification',
  'createdDays',
  'period',
  'pendingRequests',
] as const

function filtersFromParams(params: URLSearchParams): AdminRecordFilters {
  const filters: AdminRecordFilters = {}
  for (const key of FILTER_KEYS) {
    const value = params.get(key)
    if (value) filters[key] = value
  }
  return filters
}

function filterLabel(
  key: string,
  value: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (key === 'status') return t(`admin.status.${value}`)
  if (key === 'verification') {
    return t(
      value === 'verified' ? 'admin.list.verified' : 'admin.list.unverified',
    )
  }
  if (key === 'createdDays') {
    return t('admin.list.filter.createdDays', { days: value })
  }
  if (key === 'period') return t(`admin.list.period.${value}`)
  if (key === 'pendingRequests') return t('admin.list.filter.pendingRequests')
  return `${key}: ${value}`
}

/**
 * Admin drill-down record list (D-055). Filters come from the dashboard metric
 * links; search and pagination stay local like the Flutter record list.
 */
export function AdminRecordListScreen({ kind }: { kind: AdminRecordKind }) {
  const { t } = useTranslation()
  const locale = currentLocale()
  const [params] = useSearchParams()
  const filters = filtersFromParams(params)
  const filterKey = JSON.stringify(filters)
  const titleParam = params.get('title')
  const titleKey =
    titleParam && titleParam.startsWith('admin.') ? titleParam : null
  const [search, setSearch] = useState('')
  const [submittedSearch, setSubmittedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [state, setState] = useState<ListState>({ status: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    const activeFilters = JSON.parse(filterKey) as AdminRecordFilters
    const request =
      kind === 'users'
        ? adminApi
            .listUsers(
              { query: submittedSearch, filters: activeFilters, page },
              { signal: controller.signal },
            )
            .then((users) => ({ users, activities: null }))
        : adminApi
            .listActivities(
              { query: submittedSearch, filters: activeFilters, page },
              { signal: controller.signal },
            )
            .then((activities) => ({ users: null, activities }))
    request
      .then((data) => {
        if (!controller.signal.aborted) setState({ status: 'ready', ...data })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setState({ status: 'error', error })
      })
    return () => controller.abort()
  }, [filterKey, kind, page, reloadKey, submittedSearch])

  const retry = useCallback(() => {
    setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }, [])

  const submitSearch = (event: FormEvent) => {
    event.preventDefault()
    setPage(1)
    setSubmittedSearch(search)
  }

  const result = state.status === 'ready' ? (state.users ?? state.activities) : null
  const total = result?.total ?? 0
  const pageSize = result?.pageSize ?? 25
  const pageCount = total === 0 ? 1 : Math.ceil(total / pageSize)
  const emptyKey = submittedSearch.trim()
    ? 'admin.list.emptySearch'
    : 'admin.list.emptyFilter'

  return (
    <RequireAdmin>
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 pt-[calc(var(--safe-top)+16px)] pb-[calc(2rem+var(--safe-bottom))] md:px-8 md:pt-8"
      >
        <div className="mx-auto w-full max-w-[980px]">
          <header className="flex items-center gap-2">
            <Link
              to="/admin-dashboard"
              className="btn-ghost min-h-11 px-2 text-sm"
            >
              <ArrowLeft size={19} weight={kuniriIconWeight} aria-hidden="true" />
              {t('admin.list.back')}
            </Link>
          </header>

          <h1 className="mt-4 font-head text-[clamp(1.5rem,4vw,2rem)] font-extrabold text-deep">
            {titleKey
              ? t(titleKey)
              : t(
                  kind === 'users'
                    ? 'admin.list.usersTitle'
                    : 'admin.list.activitiesTitle',
                )}
          </h1>

          <form className="mt-4 flex gap-2" onSubmit={submitSearch}>
            <div className="flex min-h-12 flex-1 items-center gap-2 rounded-[16px] border border-[var(--input-border)] bg-white px-3">
              <MagnifyingGlass
                size={19}
                weight={kuniriIconWeight}
                aria-hidden="true"
                className="shrink-0 text-muted"
              />
              <label className="sr-only" htmlFor="admin-record-search">
                {t('admin.list.search')}
              </label>
              <input
                id="admin-record-search"
                type="search"
                className="min-w-0 flex-1 border-0 bg-transparent py-2 text-[15px] outline-none"
                value={search}
                placeholder={t(
                  kind === 'users'
                    ? 'admin.list.searchUsers'
                    : 'admin.list.searchActivities',
                )}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary min-h-12 px-4 text-sm">
              {t('admin.list.searchAction')}
            </button>
          </form>

          {Object.keys(filters).length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {Object.entries(filters).map(([key, value]) => (
                <li
                  key={key}
                  className="rounded-pill bg-mint px-2.5 py-1 text-xs font-bold text-teal"
                >
                  {filterLabel(key, value ?? '', t)}
                </li>
              ))}
            </ul>
          ) : null}

          {state.status === 'loading' ? (
            <div className="mt-6">
              <LoadingState labelKey="admin.list.loading" />
            </div>
          ) : state.status === 'error' ? (
            <div className="mt-6">
              <ErrorState
                error={state.error}
                titleKey="admin.list.errorTitle"
                bodyKey="admin.list.errorBody"
                onRetry={retry}
                retryLabelKey="common.retry"
              />
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm font-bold text-muted">
                {t(
                  kind === 'users'
                    ? 'admin.list.countUsers'
                    : 'admin.list.countActivities',
                  { count: total },
                )}
              </p>

              {total === 0 ? (
                <p className="mt-9 text-center text-sm text-muted">
                  {t(emptyKey)}
                </p>
              ) : kind === 'users' && state.users ? (
                <ul className="mt-2 space-y-2">
                  {state.users.items.map((row) => {
                    const name =
                      row.name.trim() ||
                      `${row.firstName} ${row.lastName}`.trim() ||
                      row.email
                    return (
                      <li
                        key={row.id}
                        className="flex items-start gap-3 rounded-[16px] border border-line bg-white p-3"
                      >
                        <span
                          className="grid size-10 shrink-0 place-items-center rounded-full bg-mint text-teal"
                          aria-hidden="true"
                        >
                          <User size={20} weight={kuniriIconWeight} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold break-words text-deep">
                            {name}
                          </p>
                          <p className="mt-0.5 text-xs leading-relaxed break-words text-muted">
                            {[
                              name !== row.email ? row.email : null,
                              row.city,
                              t(`admin.status.${row.status}`),
                              row.verifiedAt
                                ? t('admin.list.verified')
                                : t('admin.list.unverified'),
                              row.role === 'admin'
                                ? t('admin.list.administrator')
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              ) : state.activities ? (
                <ul className="mt-2 space-y-2">
                  {state.activities.items.map((row) => (
                    <li key={row.id}>
                      <Link
                        to={`/admin-dashboard/activities/${encodeURIComponent(row.id)}`}
                        className="flex items-start gap-3 rounded-[16px] border border-line bg-white p-3 hover:bg-mint"
                      >
                        <span
                          className="grid size-10 shrink-0 place-items-center rounded-full bg-mint text-teal"
                          aria-hidden="true"
                        >
                          <CalendarBlank
                            size={20}
                            weight={kuniriIconWeight}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-bold break-words text-deep">
                            {row.title || t('admin.list.untitledActivity')}
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed break-words text-muted">
                            {[
                              row.city,
                              row.creator
                                ? t('admin.list.creator', {
                                    name: row.creator,
                                  })
                                : null,
                              row.startAt
                                ? formatDateTime(row.startAt, locale)
                                : t('admin.list.noDate'),
                              t('admin.list.participants', {
                                current: row.participants,
                                max: row.capacity,
                              }),
                              t(`admin.status.${row.status}`),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                        <ArrowRight
                          size={18}
                          weight="bold"
                          aria-hidden="true"
                          className="mt-1 shrink-0 text-teal"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}

              {pageCount > 1 ? (
                <nav
                  aria-label={t('admin.list.pagination')}
                  className="mt-4 flex items-center justify-center gap-3"
                >
                  <button
                    type="button"
                    className="btn-secondary min-h-11 px-3 text-sm"
                    disabled={page <= 1}
                    aria-label={t('admin.list.previous')}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    <ArrowLeft
                      size={18}
                      weight={kuniriIconWeight}
                      aria-hidden="true"
                    />
                  </button>
                  <p className="text-sm font-bold text-deep">
                    {t('admin.list.pageOf', { page, pages: pageCount })}
                  </p>
                  <button
                    type="button"
                    className="btn-secondary min-h-11 px-3 text-sm"
                    disabled={page >= pageCount}
                    aria-label={t('admin.list.next')}
                    onClick={() =>
                      setPage((value) => Math.min(pageCount, value + 1))
                    }
                  >
                    <ArrowRight
                      size={18}
                      weight={kuniriIconWeight}
                      aria-hidden="true"
                    />
                  </button>
                </nav>
              ) : null}
            </div>
          )}
        </div>
      </main>
    </RequireAdmin>
  )
}
